<template>
  <div class="p-6">
    <Head title="Medlin Orders" />

    <h1 class="text-2xl font-bold mb-4">Orders</h1>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full table-auto border-collapse border border-gray-300">
      <thead>
      <tr class="text-left font-bold">
        <th
            class="border px-4 py-4 cursor-pointer select-none"
            @click="changeSort('id')"
        >
          <div class="flex items-center gap-1">
            id
            <span>
                    <template v-if="filters.sort === 'id'">
                      {{ filters.direction === 'asc' ? '▲' : '▼' }}
                    </template>
                    <template v-else>⇅</template>
                </span>
          </div>
        </th>
        <th
            class="border px-4 py-4 cursor-pointer select-none"
            @click="changeSort('reserved')"
        >
          <div class="flex items-center gap-1">
            Reserved
            <span>
              <template v-if="filters.sort === 'reserved'">
                {{ filters.direction === 'asc' ? '▲' : '▼' }}
              </template>
              <template v-else>⇅</template>
            </span>
          </div>
        </th>

        <th
            class="border px-4 py-4 cursor-pointer select-none"
            @click="changeSort('used')"
        >
          <div class="flex items-center gap-1">
            Used
            <span>
                <template v-if="filters.sort === 'used'">
                  {{ filters.direction === 'asc' ? '▲' : '▼' }}
                </template>
                <template v-else>⇅</template>
              </span>
          </div>
        </th>
        <th
            class="border px-4 py-4 cursor-pointer select-none"
                @click="changeSort('provider')"
            >
              <div class="flex items-center gap-1">
                  Provider
                  <span>
                    <template v-if="filters.sort === 'provider'">
                      {{ filters.direction === 'asc' ? '▲' : '▼' }}
                    </template>
                    <template v-else>⇅</template>
                </span>
            </div>
        </th>
        <th class="border px-4 py-4">OrderRef</th>
        <th class="border px-4 py-4">Link</th>
        <th class="border px-4 py-4">Method</th>
        <th class="border px-4 py-4">Period</th>
        <th class="border px-4 py-4">Amount</th>
        <th class="border px-4 py-4">Login</th>
        <th class="border px-4 py-4">Type</th>
        <th class="border px-4 py-4">Meta</th>
        <th class="border px-4 py-4">Created</th>
        <th class="border px-4 py-4">Updated</th>
        <th class="border px-4 py-4">Expires</th>

      </tr>
      </thead>
      <tbody>
      <tr v-for="order in orders.data" :key="order.id">
        <td class="border px-2 py-1">{{ order.id }}</td>
        <td class="border px-2 py-1 check-time">{{ order.reserved ? '✅' : '❌' }}</td>
        <td class="border px-2 py-1 check-time">{{ order.used ? '✅' : '❌' }}</td>
        <td class="border px-2 py-1">{{ order.provider }}</td>
        <td class="border px-2 py-1">{{ order.orderRef }}</td>
        <td class="border px-2 py-1">
            <span :title="order.link">
              {{ order.link.length > 25 ? order.link.slice(0, 25) + '…' : order.link }}
            </span>
        </td>
        <td class="border px-2 py-1">{{ order.payMethod }}</td>
        <td class="border px-2 py-1">{{ order.period }}</td>
        <td class="border px-2 py-1">{{ order.amount }}</td>
        <td class="border px-2 py-1">{{ order.login }}</td>
        <td class="border px-2 py-1">{{ order.orderType }}</td>
        <td class="border px-2 py-1">
            <span
                v-if="order.meta_data"
                :title="JSON.stringify(order.meta_data)"
            >
              {{
                JSON.stringify(order.meta_data).length > 25
                    ? JSON.stringify(order.meta_data).slice(0, 25) + '…'
                    : JSON.stringify(order.meta_data)
              }}
            </span>
        </td>
        <td class="border px-2 py-1">{{ formatDate(order.created_at) }}</td>
        <td class="border px-2 py-1">{{ formatDate(order.updated_at) }}</td>
        <td class="border px-2 py-1">{{ formatDate(order.expires_at) }}</td>
      </tr>
      </tbody>
    </table>
    </div>
    <div class="mt-4 flex justify-center gap-2">
      <div class="mt-4 flex justify-center gap-1 mt-6">
        <button
            v-for="page in orders.last_page"
            :key="page"
            @click="changePage(page)"
            :class="[
            'px-3 py-1 rounded border text-sm',
            page === orders.current_page
              ? 'bg-blue-600 border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
          ]"
        >
          {{ page }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import Layout from '@/Shared/Layout'
import VueDatepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)

export default {
  components: {
    Head,
    VueDatepicker,
  },
  layout: Layout,
  props: {
    orders: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
  },
  methods: {
    changeSort(column) {
      const isSame = this.filters.sort === column
      const direction = isSame && this.filters.direction === 'desc' ? 'asc' : 'desc'

      Inertia.get('/', {
        ...this.filters,
        sort: column,
        direction,
      }, {
        preserveState: true,
        preserveScroll: true,
      })
    },
    changePage(page) {
      Inertia.get(this.orders.path + '?page=' + page, {}, {
        preserveState: true,
        preserveScroll: true,
      })
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()
      return `${hours}:${minutes} ${day}.${month}.${year}`
    },
  },
}
</script>

<style scoped>
thead th{
  text-align: center;
}
.check-time{
  text-align: center;
}
</style>
